import { XhprofRawData, XhprofReport } from '../xhprof/XhprofReportGenerator'
import { DateTime } from './DateTime'
import { StackCall, TimelineSpan } from './TimelineSpan'
import { TraceSummary } from './TraceSummary'

export interface TraceHierarchy {
    nodes: TraceHierarchyNode[]
}

export interface TraceHierarchyNode {
    id: string
    data: TraceSummary
    children: TraceHierarchyNode[]
}

export interface SimpleTrace {
    id: string
    time: number
    transaction: string
    rawData: XhprofRawData // @todo can this be removed?
    xhprofReport: XhprofReport | null
    xhprofReportCompact: XhprofReport | null
    spans: TimelineSpan[]
    server: { name: string } | null
    service: string
    environment: string
    measureDate: DateTime
}

export interface Trace extends SimpleTrace {
    flamegraph: FlamegraphNode
    globalEvents: GlobalEvent[]
    ticks: { [id: string]: Tick }
    hidden: number
    traceId: number
    memoryLimit: boolean
    parentSpanId: string | null
}

export interface FlamegraphNode {
    name: string
    title: string
    values: TraceMetric
    children: FlamegraphNode[]
    cat: string | null
    customType: string | null
    id: string
    stats: FlamegraphNodeStats
    stack: StackCall[] | null
}

export interface DifferentialFlamegraphNode extends FlamegraphNode {
    delta: FlamegraphNodeStats
    children: DifferentialFlamegraphNode[]
    childrenFromTargetMissing: { ct: number; wt: number }
}

export interface BottleneckIdentifications {
    [identifier: string]: {
        bottleneckId: number
        id: number
        traceId: string
        data: any
        identificationDate: any
    }
}

export interface TraceMetric {
    wt: number
    mc: number
}

export type TraceDiffItem = {
    call: string
    ct: number
    wt: number
    excl_wt: number
    type: string
    cat: string
}

export type FlamegraphNodeStats = TraceMetric & { ct: number }

interface Tick {
    id: string
    summary: string
    wt: number
    ct: number
    // @todo check what's required
    selected: boolean
    odd: boolean
    height: number
    hasBottleneck: boolean
}
interface GlobalEvent {
    name: string
    ts: number
}

export interface TraceSpanCategory {
    className: string
    label: string
    fgColor?: string
}

// styling needs to be added via span-categories.less
export const traceSpanCategories: { [category: string]: TraceSpanCategory } = {
    app: { className: 'php', label: 'App' },
    php: { className: 'php', label: 'PHP' },
    'php.ctrl': { className: 'php', label: 'Controller' },
    sql: { className: 'sql', label: 'SQL' },
    view: { className: 'view', label: 'View' },
    http: { className: 'http', label: 'HTTP' },
    'http.couchdb': { className: 'http', label: 'CouchDB' },
    event: { className: 'event', label: 'Events' },
    eloquent: { className: 'doctrine', label: 'Eloquent' },
    doctrine: { className: 'doctrine', label: 'Doctrine' },
    memcache: { className: 'cache', label: 'Memcache' },
    mongo: { className: 'mongo', label: 'MongoDB' },
    mongodb: { className: 'mongo', label: 'MongoDB' },
    queue: { className: 'queue', label: 'Queue' },
    gc: { className: 'gc', label: 'GC' },
    elasticsearch: { className: 'elasticsearch', label: 'Elasticsearch' },
    redis: { className: 'redis', label: 'Redis' },
    apc: { className: 'cache', label: 'APC' },
    listener: { className: 'listener', label: 'Listener' },
    symfony: { className: 'gc', label: 'Symfony' },
    magento1: { className: 'magento', label: 'Magento1' },
    magento2: { className: 'magento', label: 'Magento2' },
    laravel: { className: 'php', label: 'Laravel' },
    wordpress: { className: 'php', label: 'Wordpress' },
    shopware: { className: 'php', label: 'Shopware' },
    shopware6: { className: 'php', label: 'Shopware 6' },
    oxid: { className: 'php', label: 'Oxid' },
    yii: { className: 'php', label: 'Yii' },
    flow: { className: 'php', label: 'FLOW' },
    spryker: { className: 'spryker', label: 'Spryker' },
    propel: { className: 'doctrine', label: 'Propel' },
    shell: { className: 'shell', label: 'Shell' },
    graphql: { className: 'graphql', label: 'GraphQL' },
    template: { className: 'view', label: 'Template' },
    swdal: { className: 'php', label: 'DAL' },
    phpunit: { className: 'php', label: 'PHPUnit' },
    dns: { className: 'dns', label: 'DNS', fgColor: '#1c9295' },
    email: { className: 'email', label: 'Email' },
}

export const getTraceSpanCategory = (span: TimelineSpan): TraceSpanCategory =>
    getTraceSpanCategoryRaw(span.cat, span?.annotations?.custom_type)

export const getTraceSpanCategoryFromFlamegraphNode = (node: FlamegraphNode): TraceSpanCategory =>
    getTraceSpanCategoryRaw(node.cat || 'php', node.customType)

const getTraceSpanCategoryRaw = (cat: string, customType: string | null): TraceSpanCategory => {
    if (cat in traceSpanCategories) {
        return traceSpanCategories[cat]
    }

    let label: string = cat
    if (cat === 'custom' && customType) {
        label = customType
    }

    return { className: 'php', label: `Custom [${label}]` }
}

export const traceHasCallgraphData = (trace: Trace | SimpleTrace) => (trace.rawData ? Object.keys(trace.rawData).length > 1 : false)

export const findFlamegraphNode = (node: FlamegraphNode, nodeId: string): FlamegraphNode | DifferentialFlamegraphNode | null => {
    if (node.id === nodeId) {
        return node
    }

    for (const child of node.children) {
        const childNode = findFlamegraphNode(child, nodeId)
        if (childNode !== null) {
            return childNode
        }
    }

    return null
}

/*
What is delta?

| Compare | Base | Delta | % and actual message in frame  |
|---------|------|-------|--------------------------------|
| 100     | 300  | -200  | -66% faster in target          |
| 100     | 200  | -100  | -50% faster in target          |
| 300     | 100  | +200  | 200% slower in target          |
| 200     | -    | +200  | only in target                 |
|---------|------|-------|--------------------------------|

Compare is what in the UI is called 'Target'
 */
export const diffFlamegraphs = (compare: FlamegraphNode, base?: FlamegraphNode): DifferentialFlamegraphNode => {
    if (!base) {
        return {
            ...compare,
            childrenFromTargetMissing: { ct: 0, wt: 0 },
            delta: {
                wt: compare.values.wt,
                mc: compare.values.mc,
                ct: compare.stats.ct,
            },
            children: compare.children.map(child => diffFlamegraphs(child)),
        }
    }

    const childrenFromTargetMissing = base.children.reduce(
        (carry, baseChild) => {
            if (!compare.children.find(compareChild => compareChild.name === baseChild.name)) {
                carry = {
                    ct: carry.ct + numberOfChildren(baseChild),
                    wt: carry.wt + baseChild.values.wt,
                }
            }

            return carry
        },
        { ct: 0, wt: 0 },
    )

    return {
        ...compare,
        childrenFromTargetMissing,
        delta: {
            wt: compare.values.wt - base.values.wt,
            mc: compare.values.mc - base.values.mc,
            ct: compare.stats.ct - base.stats.ct,
        },
        children: compare.children.map(compareChild =>
            diffFlamegraphs(
                compareChild,
                base.children.find(baseChild => baseChild.name === compareChild.name),
            ),
        ),
    }
}

export const isDifferentialFlamegraphNode = (node: FlamegraphNode | DifferentialFlamegraphNode): node is DifferentialFlamegraphNode =>
    'delta' in node

export const differentialFlamegraphMissingStats = (node: DifferentialFlamegraphNode): { ct: number; wt: number } =>
    node.children.reduce((carry, child: DifferentialFlamegraphNode) => {
        const childStats = differentialFlamegraphMissingStats(child)
        return {
            ct: carry.ct + childStats.ct,
            wt: carry.wt + childStats.wt,
        }
    }, node.childrenFromTargetMissing)

export const differentialFlamegraphBaseValue = (targetValue: number, delta: number): number => targetValue - delta

export const differentialFlamegraphPercentageChange = (targetValue: number, delta: number): number =>
    delta / differentialFlamegraphBaseValue(targetValue, delta)

const numberOfChildren = (node: FlamegraphNode): number => node.children.reduce((carry, child) => carry + numberOfChildren(child), 1)
